import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, AlertTriangle, Loader2 } from 'lucide-react';
import { AuthLayout } from '../../components/auth/AuthLayout';
import { authApi, readApiError } from '../../lib/auth-api';

type Stage = 'verifying' | 'success' | 'error';

export function VerifyEmailPage() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const navigate = useNavigate();
  const [stage, setStage] = useState<Stage>(token ? 'verifying' : 'error');
  const [message, setMessage] = useState<string>(token ? '' : t('auth.errorTokenMissing'));
  const [resendBusy, setResendBusy] = useState(false);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    authApi
      .verifyEmail(token)
      .then(({ user }) => {
        if (cancelled) return;
        setStage('success');
        setTimeout(() => navigate(user.companyId ? '/app' : '/onboarding'), 1500);
      })
      .catch((e) => {
        if (cancelled) return;
        setStage('error');
        setMessage(readApiError(e, t('auth.errorTokenInvalid')));
      });
    return () => { cancelled = true; };
  }, [token, navigate, t]);

  async function resend() {
    setResendBusy(true);
    try {
      await authApi.resendVerification();
      setMessage(t('auth.verifyResentBody'));
    } catch (e) {
      setMessage(readApiError(e, t('auth.errorGeneric')));
    } finally {
      setResendBusy(false);
    }
  }

  return (
    <AuthLayout
      eyebrow={t('auth.verifyEyebrow')}
      title={
        stage === 'verifying'
          ? t('auth.verifyingTitle')
          : stage === 'success'
          ? t('auth.verifySuccessTitle')
          : t('auth.verifyFailedTitle')
      }
    >
      <div className="flex flex-col items-center text-center gap-4">
        {stage === 'verifying' && (
          <>
            <Loader2 size={40} className="text-accent-600 animate-spin" />
            <p className="text-sm text-muted-foreground">{t('auth.verifyingBody')}</p>
          </>
        )}
        {stage === 'success' && (
          <>
            <CheckCircle2 size={40} className="text-emerald-600" />
            <p className="text-sm text-muted-foreground">{t('auth.verifySuccessBody')}</p>
          </>
        )}
        {stage === 'error' && (
          <>
            <AlertTriangle size={40} className="text-warn-600" />
            <p className="text-sm text-destructive">{message}</p>
            <button
              type="button"
              onClick={resend}
              disabled={resendBusy}
              className="inline-flex items-center justify-center gap-2 h-10 px-4 rounded-lg border border-border bg-card hover:bg-muted text-sm font-medium disabled:opacity-60"
            >
              {resendBusy && <Loader2 size={14} className="animate-spin" />}
              {t('auth.resendVerification')}
            </button>
            <Link to="/login" className="text-sm text-accent-700 hover:underline">
              {t('auth.signInLink')}
            </Link>
          </>
        )}
      </div>
    </AuthLayout>
  );
}
